import { useState, useEffect, useCallback } from "react";
import { isHumanAgentInboxLive } from "@/api/inboxAgentApi";
import { fetchInboxCustomerHistory } from "@/api/inboxCustomerHistoryApi";
import {
  getHistoricThreads,
  getCustomerHistoryAiSummary,
  mapInboxCustomerHistoryToUiPayload,
} from "@/api/customerHistoryApi";

/**
 * Historic threads + AI customer summary. One live request to human-agent; stub uses mock graph.
 * @param {string | null | undefined} conversationId
 * @param {{
 *   workspaceId?: string | null,
 *   centralInbox?: boolean,
 *   sessionWorkspaceId?: string | null,
 * }} [options]
 */
export function useCustomerHistory(conversationId, options = {}) {
  const { workspaceId, centralInbox = false, sessionWorkspaceId = null } = options;
  const [historicThreads, setHistoricThreads] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setHistoricThreads([]);
      setAiSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (isHumanAgentInboxLive() && (centralInbox || workspaceId)) {
        const raw = await fetchInboxCustomerHistory({
          sessionId: conversationId,
          scope: centralInbox ? "central" : undefined,
          workspaceId: centralInbox ? null : workspaceId,
          sessionWorkspaceId: centralInbox ? sessionWorkspaceId : null,
        });
        const { historicThreads: ht, aiSummary: asum } = mapInboxCustomerHistoryToUiPayload(raw);
        setHistoricThreads(ht);
        setAiSummary(asum);
      } else {
        const [threadsRes, summaryRes] = await Promise.all([
          getHistoricThreads({ conversationId }),
          getCustomerHistoryAiSummary({ conversationId }),
        ]);
        setHistoricThreads(threadsRes.data);
        setAiSummary(summaryRes.data);
      }
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setHistoricThreads([]);
      setAiSummary(null);
    } finally {
      setLoading(false);
    }
  }, [conversationId, workspaceId, centralInbox, sessionWorkspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { historicThreads, aiSummary, loading, error, refresh };
}
